import React, { useState } from 'react';
import { Box, Button, Typography, Paper, Alert, CircularProgress, Chip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

const PolicyUpload = ({ apiUrl = 'http://localhost:8000' }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [kbInfo, setKbInfo] = useState(null);

  // Fetch knowledge base info
  const fetchKBInfo = async () => {
    try {
      const response = await fetch(`${apiUrl}/knowledge-base/info`);
      const data = await response.json();
      setKbInfo(data.knowledge_base);
    } catch (err) {
      console.error('Failed to fetch KB info:', err);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.pdf') && !file.name.endsWith('.txt')) {
      setError('يُسمح فقط بملفات PDF و TXT - Only PDF and TXT files are allowed');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${apiUrl}/upload-policy`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      setUploadResult(result);
      fetchKBInfo(); // Refresh KB info
    } catch (err) {
      setError(`فشل تحميل الملف: ${err.message} - Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Clear knowledge base
  const handleClearKB = async () => {
    if (!window.confirm('هل أنت متأكد من مسح جميع السياسات؟\nAre you sure you want to clear all policies?')) {
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/knowledge-base/clear`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Clear failed');
      }

      const result = await response.json();
      setUploadResult(null);
      setKbInfo(null);
      alert(result.message);
      fetchKBInfo();
    } catch (err) {
      setError(`فشل المسح: ${err.message} - Clear failed: ${err.message}`);
    }
  };

  // Load KB info on mount
  React.useEffect(() => {
    fetchKBInfo();
  }, []);

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionIcon />
        تحميل السياسات - Policy Upload
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        قم بتحميل ملفات السياسات (PDF/TXT) لاستخدامها في الإجابة على الأسئلة
        <br />
        Upload policy documents (PDF/TXT) to use in answering questions
      </Typography>

      {/* Knowledge Base Info */}
      {kbInfo && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'white', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InfoIcon fontSize="small" />
            قاعدة المعرفة الحالية - Current Knowledge Base
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`${kbInfo.total_documents} مستند / Documents`} size="small" />
            <Chip label={`${kbInfo.total_chunks} جزء / Chunks`} size="small" />
            {kbInfo.sources && kbInfo.sources.length > 0 && (
              <Chip 
                label={`المصادر / Sources: ${kbInfo.sources.join(', ')}`} 
                size="small" 
                color="primary"
              />
            )}
          </Box>
        </Box>
      )}

      {/* Upload Button */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          component="label"
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          disabled={uploading}
        >
          {uploading ? 'جاري التحميل... / Uploading...' : 'تحميل ملف / Upload File'}
          <input
            type="file"
            hidden
            accept=".pdf,.txt"
            onChange={handleFileUpload}
            disabled={uploading}
          />
        </Button>

        {kbInfo && kbInfo.total_documents > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleClearKB}
          >
            مسح الكل / Clear All
          </Button>
        )}

        <Button
          variant="outlined"
          startIcon={<InfoIcon />}
          onClick={fetchKBInfo}
        >
          تحديث / Refresh
        </Button>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success Message */}
      {uploadResult && uploadResult.status === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadResult(null)}>
          <Typography variant="body2" fontWeight="bold">
            {uploadResult.message}
          </Typography>
          <Typography variant="caption">
            الملف: {uploadResult.filename}
            {uploadResult.details && (
              <>
                <br />
                الأجزاء المنشأة: {uploadResult.details.chunks_created}
                {uploadResult.details.pages !== 'N/A' && ` | الصفحات: ${uploadResult.details.pages}`}
                <br />
                طول النص: {uploadResult.details.text_length} حرف
              </>
            )}
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default PolicyUpload;
